import type { MessageKey } from './types'
import type { useT } from './use-t'

type ErrorKey = Extract<MessageKey, `errors.${string}`>
const codes: Record<string, ErrorKey> = {
  NETWORK: 'errors.network',
  EMAIL_EXISTS: 'errors.emailTaken',
  INVITE_INVALID: 'errors.registration',
  EMAIL_VERIFICATION_REQUIRED: 'errors.verify',
  ADMIN_REQUIRED: 'errors.forbidden',
  SESSION_CHANGED: 'errors.login',
  INVALID_CURRENT_PASSWORD: 'errors.credentials',
  SOURCE_CHANGED: 'errors.conflict',
  ADMIN_CONFLICT: 'errors.conflict',
  TOKEN_CONFLICT: 'errors.conflict',
  TASK_STATE_CONFLICT: 'errors.conflict',
  RECEIPT_MISMATCH: 'errors.confirm',
  INVALID_MEDIA: 'errors.fileType',
  INVALID_MEDIA_TYPE: 'errors.fileType',
  MEDIA_TOO_LARGE: 'errors.fileSize',
  MEDIA_BUSY: 'errors.unavailable',
  PAYMENT_NOT_CONFIGURED: 'errors.unavailable',
  TRAILER_ALREADY_AVAILABLE: 'errors.conflict',
  TELEGRAM_ALREADY_BOUND: 'errors.conflict',
  TELEGRAM_BINDING_NOT_FOUND: 'errors.notFound',
  NO_ELIGIBLE_CONTENT: 'errors.notFound',

  BAD_RESPONSE: 'errors.response',
  RESPONSE_TOO_LARGE: 'errors.response',
  IDENTITY_CHANGED: 'errors.identity',
  UNAUTHENTICATED: 'errors.login',
  INVALID_CREDENTIALS: 'errors.credentials',
  INVALID_EMAIL: 'errors.email',
  EMAIL_TAKEN: 'errors.emailTaken',
  WEAK_PASSWORD: 'errors.password',
  INVALID_INVITE: 'errors.registration',
  INVALID_INVITE_CODE: 'errors.registration',
  INVITE_REQUIRED: 'errors.registration',
  REGISTRATION_LIMIT_REACHED: 'errors.registration',
  EMAIL_NOT_VERIFIED: 'errors.verify',
  EMAIL_UNVERIFIED: 'errors.verify',
  FORBIDDEN: 'errors.forbidden',
  NOT_FOUND: 'errors.notFound',
  CONFLICT: 'errors.conflict',
  IDEMPOTENCY_CONFLICT: 'errors.conflict',
  SNAPSHOT_CHANGED: 'errors.conflict',
  TASK_ALREADY_ACTIVE: 'errors.conflict',
  RATE_LIMITED: 'errors.rate',
  TOO_MANY_REQUESTS: 'errors.rate',
  QUOTA_EXCEEDED: 'errors.quota',
  QUOTA_EXHAUSTED: 'errors.quota',
  SUBSCRIPTION_LIMIT: 'errors.subscriptions',
  SUBSCRIPTION_LIMIT_REACHED: 'errors.subscriptions',
  PAID_MEMBERSHIP_REQUIRED: 'errors.paid',
  INVALID_FILE_SIZE: 'errors.fileSize',
  INVALID_FILE_TYPE: 'errors.fileType',
  INVALID_CHECKOUT_URL: 'errors.checkout',
  INVALID_INPUT: 'errors.input',
  INVALID_PASSWORD: 'errors.password',
  INVALID_TOKEN: 'errors.expired',
  TOKEN_EXPIRED: 'errors.expired',
  INVALID_CDKEY: 'errors.expired',
  CDKEY_EXPIRED: 'errors.expired',
  CDKEY_USED: 'errors.codeUsed',
  NOT_CONFIRMED: 'errors.confirm',
  ORDER_MISMATCH: 'errors.confirm',
  OUTCOME_UNKNOWN: 'errors.unknown',
  TRAILER_OUTCOME_UNKNOWN: 'errors.unknown',
  OBSERVATION_FAILED: 'errors.unavailable',
  PROVIDER_UNAVAILABLE: 'errors.unavailable',
}
const keys = new Set<string>([...Object.values(codes), 'errors.request'])

// Keep stable keys in async state. Translate when rendering so an existing
// error also follows a language change; never render provider error prose.
export function errorKey(error: unknown): ErrorKey {
  if (typeof error === 'string') {
    if (keys.has(error)) return error as ErrorKey
    return codes[error] ?? 'errors.request'
  }
  if (error && typeof error === 'object') {
    if ('name' in error && error.name === 'UnknownOutcomeError') return 'errors.unknown'
    const code = 'code' in error ? String(error.code) : ''
    if (codes[code]) return codes[code]
    if (code.endsWith('_UNAVAILABLE')) return 'errors.unavailable'
    const status = 'status' in error ? error.status : 0
    if (status === 401) return 'errors.login'
    if (status === 403) return 'errors.forbidden'
    if (status === 404) return 'errors.notFound'
    if (status === 409) return 'errors.conflict'
    if (status === 429) return 'errors.rate'
    if (typeof status === 'number' && status >= 500) return 'errors.unavailable'
  }
  return 'errors.request'
}

export function errorText(error: unknown, t: ReturnType<typeof useT>): string {
  return t(errorKey(error))
}

const statuses = [
  'queued',
  'running',
  'retry_scheduled',
  'succeeded',
  'failed',
  'dead_letter',
  'cancelled',
  'PENDING',
  'PAID',
  'CANCELLED',
  'MANUAL',
  'CDKEY',
  'CCPAYMENT',
] as const
export function statusText(status: string, t: ReturnType<typeof useT>): string {
  const key = statuses.find((value) => value === status)
  return key ? t(`status.${key}`) : status
}

const taskKinds: Record<string, MessageKey> = {
  crawl_videos_batch: 'admin7.recent',
  crawler_actress: 'admin7.actressCrawl',
  translate_video: 'admin7.translateVideo',
  batch_translate: 'admin7.translateBatch',
  catalog_video_refresh: 'admin7.videoRefresh',
  catalog_actress_refresh: 'admin7.actressRefresh',
  catalog_trailer_maintain: 'admin7.trailerMaintenance',
  catalog_magnet_repair: 'video.refreshMagnets',
  catalog_media_repair: 'admin7.trailerMaintenance',
}
export function taskText(kind: string, t: ReturnType<typeof useT>): string {
  const key = taskKinds[kind.replace(/^rebuild_/, '')]
  return key ? t(key) : kind
}
